import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { UserGroup } from '@gmn-portal/backend';
import {
  AlertCircle,
  Calendar,
  GripHorizontal,
  Hash,
  Images,
  Link2,
  Pencil,
  SeparatorHorizontal,
  Star,
  ToggleLeft,
  Trash2,
  Type,
} from 'lucide-react';
import { useIntl } from '@/core/i18n/use-intl';
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui';
import { cn } from '@/shared/utils';
import type { FieldError, MetadataField } from './types';

interface MetadataFieldCardProps {
  field: MetadataField;
  userGroups?: UserGroup[];
  disabled?: boolean;
  onEdit: () => void;
  onDelete: () => void;
  /** Validation error for this field */
  error?: FieldError;
}

const typeIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  string: Type,
  number: Hash,
  boolean: ToggleLeft,
  date: Calendar,
  section: SeparatorHorizontal,
  'relation-display': Link2,
  photos: Images,
};

export function MetadataFieldCard({
  field,
  disabled,
  onEdit,
  onDelete,
  error,
}: MetadataFieldCardProps) {
  const { t } = useIntl();
  const isSection = field.type === 'section';
  const hasError = !!error;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const TypeIcon = typeIcons[field.type] || Type;

  // Create a stable test id based on field label
  const fieldTestId = (field.labelDe || field.id)
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`metadata-field-card-${fieldTestId}`}
      className={cn(
        'group relative flex items-center gap-3 p-3 rounded-lg border transition-all',
        isDragging && 'opacity-40 bg-muted border-primary border-dashed',
        hasError && 'border-destructive bg-destructive/5',
        !hasError &&
          (isSection
            ? 'bg-muted/50 border-muted-foreground/20'
            : 'bg-background border-border hover:border-primary/50 hover:shadow-sm')
      )}
    >
      {/* Drag Handle */}
      {!disabled && (
        <div
          {...attributes}
          {...listeners}
          className="cursor-move text-muted-foreground/50 hover:text-muted-foreground touch-none select-none"
        >
          <GripHorizontal size={16} />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 grid grid-cols-[1fr_140px_56px_48px] gap-3 items-center min-w-0">
        {/* Labels */}
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'font-medium truncate',
                isSection ? 'text-muted-foreground text-sm' : 'text-foreground text-sm',
                hasError && 'text-destructive'
              )}
            >
              {field.labelDe || t('metadata.builder.untitledField')}
            </span>
            {!isSection && field.filterFavorite && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Star className="size-3.5 text-muted-foreground shrink-0" />
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p className="text-sm">{t('metadata.builder.filterFavorite')}</p>
                </TooltipContent>
              </Tooltip>
            )}
            {hasError && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertCircle className="size-4 text-destructive shrink-0" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="text-sm">{error.message}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          {!isSection && field.labelEn && !hasError && (
            <span className="text-xs text-muted-foreground truncate">{field.labelEn}</span>
          )}
          {hasError && <span className="text-xs text-destructive truncate">{error.message}</span>}
        </div>

        {/* Type Badge - fixed width */}
        <div className="flex justify-start min-w-0">
          <span
            data-testid={`metadata-field-type-badge-${fieldTestId}`}
            title={t(`metadata.fieldTypes.${field.type}`)}
            className={cn(
              'inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium max-w-full overflow-hidden',
              isSection
                ? 'bg-muted-foreground/10 text-muted-foreground'
                : hasError && error.errorType === 'type'
                  ? 'bg-destructive/10 text-destructive'
                  : 'bg-primary/10 text-primary'
            )}
          >
            <TypeIcon className="size-3 shrink-0" />
            <span className="min-w-0 truncate">{t(`metadata.fieldTypes.${field.type}`)}</span>
          </span>
        </div>

        {/* Badges - fixed width for alignment */}
        <div className="flex items-center justify-center gap-1.5">
          {!isSection ? (
            <span
              title={field.required ? t('common.required') : t('common.optional')}
              className={cn(
                'inline-flex items-center justify-center size-5 rounded text-xs font-bold',
                field.required
                  ? hasError && error.errorType === 'required'
                    ? 'bg-destructive text-destructive-foreground'
                    : 'bg-destructive/10 text-destructive'
                  : 'bg-transparent text-transparent'
              )}
            >
              *
            </span>
          ) : (
            <span className="size-5" />
          )}
        </div>

        {/* Width Indicator - fixed width */}
        <div className="flex justify-end">
          <span className="text-xs text-muted-foreground font-mono border border-border rounded px-1.5 py-0.5">
            {field.width}%
          </span>
        </div>
      </div>

      {/* Actions - always visible */}
      {!disabled && (
        <div className="flex items-center gap-1 border-l border-border pl-3">
          <Button
            variant="ghost"
            size="icon"
            className="size-7 text-muted-foreground hover:text-primary"
            onClick={onEdit}
            data-testid={`metadata-field-edit-${fieldTestId}`}
          >
            <Pencil size={14} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7 text-muted-foreground hover:text-destructive"
            onClick={onDelete}
            data-testid={`metadata-field-delete-${fieldTestId}`}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      )}
    </div>
  );
}
